import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Building2, User, Lock } from "lucide-react";
import { toast } from "sonner";
import { MfaSetup, MfaChallenge, DEVICE_KEY } from "@/components/auth/Mfa";
import { resetPermissions } from "@/hooks/usePermissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const [mfa, setMfa] = useState(null); // { mode: "setup"|"challenge", token, emailHint }
  const [loginData, setLoginData] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);

  const finish = (data) => {
    if (data.device_token) localStorage.setItem(DEVICE_KEY, data.device_token);
    resetPermissions();
    toast.success("Login successful!");
    onLogin(data.user, data.access_token);
  };
  const route = (data) => {
    if (data.mfa_setup_required) return setMfa({ mode: "setup", token: data.mfa_token });
    if (data.mfa_required) return setMfa({ mode: "challenge", token: data.mfa_token, emailHint: data.email_hint });
    finish(data);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { ...loginData, device_token: localStorage.getItem(DEVICE_KEY) || null });
      route(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  if (mfa) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)'}}>
        <Card className="max-w-md w-full bg-white border-0 shadow-2xl"><CardContent className="p-6">
          {mfa.mode === "setup"
            ? <MfaSetup mfaToken={mfa.token} onComplete={finish} onCancel={() => setMfa(null)} />
            : <MfaChallenge mfaToken={mfa.token} emailHint={mfa.emailHint} onComplete={finish} onBack={() => setMfa(null)} />}
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)'}}>
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <div className="bg-white/10 backdrop-blur-xl p-4 rounded-2xl shadow-lg border border-white/20">
              <Building2 className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">
            Invoice Tracker
          </h1>
          <p className="text-blue-100">
            Manage customers, invoices, and appointments
          </p>
        </div>

        <Card className="bg-white/95 backdrop-blur-xl shadow-2xl border-white/20">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4" data-testid="login-tab">Login</h2>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="login-username">Username</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="login-username"
                    data-testid="login-username-input"
                    value={loginData.username}
                    onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                    required
                    className="pl-10"
                    placeholder="Enter your username"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="login-password">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="login-password"
                    type="password"
                    data-testid="login-password-input"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    className="pl-10"
                    placeholder="Enter your password"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {loading ? "Logging in..." : "Login"}
              </Button>
            </form>
            <p className="text-xs text-slate-500 text-center mt-4" data-testid="login-no-register-note">Accounts are created by an administrator in Settings → Users.</p>
          </CardContent>
        </Card>

        <p className="text-center text-blue-100 text-sm mt-6">
          Secure authentication with JWT tokens
        </p>
      </div>
    </div>
  );
}
